import { motion } from 'framer-motion'
import { AlertCircle } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formStyles } from '@/constants/styles'
import { AssetsInputWithTags } from './AssetsInputWithTags'
import { INDUSTRIES_OF_INTEREST, INVESTMENT_AMOUNT_OPTIONS, POPULAR_ASSETS_BUY, POPULAR_ASSETS_SELL } from './ProfilePreferencesScreenBase'
import type { ProfilePreferencesScreenBaseProps } from './ProfilePreferencesScreenBase'

// Background images from Figma
const imgEllipse947 = "https://www.figma.com/api/mcp/asset/a7508128-3eb4-4f52-8aa4-d389b97cd03a"
const imgGroup691314559 = "https://www.figma.com/api/mcp/asset/e60261da-aeee-4456-88a9-e02ab77c1c6a"

export const ProfilePreferencesScreenDesktop = ({
  formData,
  loading,
  error,
  onFormDataChange,
  onIndustryToggle,
  onSelectAllIndustries,
  onClearAllIndustries,
  onSubmit,
}: ProfilePreferencesScreenBaseProps) => {
  const handleInputChange = (field: keyof typeof formData, value: string) => {
    onFormDataChange({ [field]: value })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gradient-to-b from-[#f2f1f6] to-[#eae9ec] relative flex flex-col items-center justify-start min-h-screen py-20 overflow-x-hidden"
    >
      {/* Background ellipse */}
      <div className="absolute bottom-[321px] left-0 size-[985px] pointer-events-none">
        <div className="absolute inset-[-20.3%]">
          <img 
            alt="" 
            className="block max-w-none w-full h-full object-cover" 
            src={imgEllipse947} 
          />
        </div>
      </div>

      {/* Title & Subtitle */}
      <div className="relative z-10 text-center mb-12">
        <h1 className="font-plus-jakarta font-medium leading-[48px] text-[#2d2923] text-[36px] tracking-[-0.52px] mb-2">
          Account Verification Required
        </h1>
        <p className="font-plus-jakarta font-normal leading-[24px] text-[#2d2923] text-[16px]">
          Please complete the following attestation to access your account
        </p>
      </div>

      {/* Main Card */}
      <div className="relative bg-white border-2 border-solid border-white w-full max-w-[750px] rounded-xl shadow-xl overflow-hidden mb-20">
        {/* Background image inside card */}
        <div className="absolute bottom-[-2px] h-[800px] left-[-258px] w-[1222px] pointer-events-none opacity-20">
          <div className="absolute inset-[-13%_-8.51%_-14.25%_-8.51%]">
            <img 
              alt="" 
              className="block max-w-none w-full h-full object-cover" 
              src={imgGroup691314559} 
            />
          </div>
        </div>

        <form onSubmit={onSubmit} className="relative z-10 p-12 space-y-10">
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 flex items-center gap-2 rounded">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Profile Information Section */}
          <section className="space-y-6">
            <h2 className="font-plus-jakarta font-medium text-[20px] text-[#2d2923] leading-[24px] tracking-[-0.2px]">
              Profile Information
            </h2>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="font-plus-jakarta font-medium text-[14px] text-[#2d2923] leading-[20px]">
                  First name
                </label>
                <Input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  placeholder="Enter your first name"
                  className={formStyles.input}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="font-plus-jakarta font-medium text-[14px] text-[#2d2923] leading-[20px]">
                  Last Name
                </label>
                <Input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  placeholder="Enter your last name"
                  className={formStyles.input}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="font-plus-jakarta font-medium text-[14px] text-[#2d2923] leading-[20px]">
                  Job Title
                </label>
                <Input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => handleInputChange('job_title', e.target.value)}
                  placeholder="e.g., Investment Manager, CEO"
                  className={formStyles.input}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="font-plus-jakarta font-medium text-[14px] text-[#2d2923] leading-[20px]">
                  Company Name
                </label>
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Enter your company name"
                  className={formStyles.input}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="font-plus-jakarta font-medium text-[14px] text-[#2d2923] leading-[20px]">
                Country
              </label>
              <Select
                value={formData.country}
                onValueChange={(value) => handleInputChange('country', value)}
              >
                <SelectTrigger className="w-full h-[48px] pl-[12px] pr-[18px] py-[14px] !bg-white !border-[rgba(45,41,35,0.12)] border-solid text-[#2d2923] font-plus-jakarta font-medium text-[14px] leading-[20px] hover:!bg-white focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)]">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
              <SelectContent className="!bg-white text-[#2d2923] border-[rgba(45,41,35,0.12)]">
                <SelectItem value="United States">United States</SelectItem>
                <SelectItem value="United Arab Emirates">UAE</SelectItem>
                <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                <SelectItem value="Australia">Australia</SelectItem>
                <SelectItem value="Brazil">Brazil</SelectItem>
                <SelectItem value="Canada">Canada</SelectItem>
                <SelectItem value="France">France</SelectItem>
                <SelectItem value="Germany">Germany</SelectItem>
                <SelectItem value="Hong Kong">Hong Kong</SelectItem>
                <SelectItem value="India">India</SelectItem>
                <SelectItem value="Japan">Japan</SelectItem>
                <SelectItem value="Singapore">Singapore</SelectItem>
                <SelectItem value="South Korea">South Korea</SelectItem>
                <SelectItem value="Switzerland">Switzerland</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
              </Select>
            </div>
          </section>

          {/* Interests Section */}
          <section className="space-y-8">
            <div className="space-y-4">
              <AssetsInputWithTags
                label="Share the assets you're interested in buying to receive exclusive alerts"
                value={formData.companies_of_interest_buy}
                onChange={(assets) => onFormDataChange({ companies_of_interest_buy: assets })}
                placeholder="e.g., Ethereum, Solana, Polygon"
                quickOptions={POPULAR_ASSETS_BUY}
              />
            </div>

            <div className="space-y-4">
              <AssetsInputWithTags
                label="Let us know which assets you're planning to sell"
                value={formData.companies_of_interest_sell}
                onChange={(assets) => onFormDataChange({ companies_of_interest_sell: assets })}
                placeholder="e.g., Bitcoin, Cardano"
                quickOptions={POPULAR_ASSETS_SELL}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <label className="font-plus-jakarta font-medium text-[14px] text-[#2d2923] leading-[20px]">
                    What categories excite you?
                  </label>
                  <p className="font-plus-jakarta font-normal text-[12px] text-[#2d2923] opacity-70 leading-[16px]">
                    Let us know to unlock relevant resources
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); onSelectAllIndustries() }}
                    className="font-plus-jakarta font-medium text-[13px] text-[#2d2923] underline hover:opacity-80"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); onClearAllIndustries() }}
                    className="font-plus-jakarta font-medium text-[13px] text-[#2d2923] underline hover:opacity-80"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              
              <div className="bg-[rgba(255,255,255,0.32)] border border-[rgba(45,41,35,0.12)] p-4 rounded-lg">
                <div className="flex flex-wrap gap-2">
                  {INDUSTRIES_OF_INTEREST.map((industry) => (
                    <button
                      key={industry}
                      type="button"
                      onClick={() => onIndustryToggle(industry)}
                      className={`px-3 py-2 rounded-full font-plus-jakarta font-medium text-[13px] leading-[18px] transition-all border ${
                        formData.industries_of_interest.includes(industry)
                          ? 'bg-[#2d2923] text-white border-[#2d2923]'
                          : 'bg-white text-[#2d2923] border-[rgba(45,41,35,0.12)] hover:bg-gray-50'
                      }`}
                    >
                      {industry}
                    </button>
                  ))}
                </div>
                {formData.industries_of_interest.length > 0 && (
                  <p className="mt-3 font-plus-jakarta font-normal text-[12px] text-[rgba(45,41,35,0.72)]">
                    Selected: {formData.industries_of_interest.length} of {INDUSTRIES_OF_INTEREST.length}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Investment Preferences Section */}
          <section className="space-y-6">
            <h2 className="font-plus-jakarta font-medium text-[20px] text-[#2d2923] leading-[24px] tracking-[-0.2px]">
              Investment Amount Preferences
            </h2>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="font-plus-jakarta font-medium text-[14px] text-[#2d2923] leading-[20px]">
                  What is your minimum investment amount?
                </label>
                <Select
                  value={formData.preferred_investment_amount_min?.toString() || ''}
                  onValueChange={(value) => onFormDataChange({ 
                    preferred_investment_amount_min: value ? parseInt(value) : undefined 
                  })}
                >
                  <SelectTrigger className="w-full h-[52px] !bg-white !border-[rgba(45,41,35,0.12)] text-[#2d2923] font-plus-jakarta font-medium text-[14px]">
                    <SelectValue placeholder="Select minimum amount" />
                  </SelectTrigger>
                  <SelectContent className="!bg-white text-[#2d2923] border-[rgba(45,41,35,0.12)]">
                    {INVESTMENT_AMOUNT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="font-plus-jakarta font-medium text-[14px] text-[#2d2923] leading-[20px]">
                  What is your maximum investment amount?
                </label>
                <Select
                  value={formData.preferred_investment_amount_max?.toString() || ''}
                  onValueChange={(value) => onFormDataChange({ 
                    preferred_investment_amount_max: value ? parseInt(value) : undefined 
                  })}
                >
                  <SelectTrigger className="w-full h-[52px] !bg-white !border-[rgba(45,41,35,0.12)] text-[#2d2923] font-plus-jakarta font-medium text-[14px]">
                    <SelectValue placeholder="Select maximum amount" />
                  </SelectTrigger>
                  <SelectContent className="!bg-white text-[#2d2923] border-[rgba(45,41,35,0.12)]">
                    {INVESTMENT_AMOUNT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="pt-6">
            <Button
              type="submit"
              variant="primary"
              className="w-full h-14 bg-[#2d2923] hover:bg-[#1a1612] text-white font-plus-jakarta font-medium text-[16px] rounded-lg transition-all"
              isLoading={loading}
            >
              SAVE PREFERENCES
            </Button>
            <p className="mt-6 font-plus-jakarta font-medium text-[14px] text-[rgba(45,41,35,0.72)] text-center">
              You can always update these preferences later in your profile settings.
            </p>
          </div>
        </form>
      </div>
    </motion.div>
  )
}

